'use client';

/**
 * VerticalHero — shared hero used by all V3 vertical pages.
 *
 * Visual: gradient background, soft accent halo on the right, mono
 * kicker, two-clause headline (white + desaturated), subhead, two CTAs.
 *
 * Per-vertical variation comes through props:
 *   - `accent` drives kicker color and halo tint (RGB derived from hex).
 *   - `copy` provides every visible string + CTA hrefs + the
 *     section's aria-label.
 *   - When `copy.image` is set, the visual is rendered to the right
 *     of the copy on lg+, stacked under it on mobile.
 *
 * No client interactivity, but kept as a client component for parity
 * with the buttons / Link components and to match the legacy verticals.
 */

import Image from 'next/image';
import CtaLink from '@/components/common/CtaLink';
import React from 'react';
import type { HeroCopy, VerticalAccent } from './types';

type Props = {
  accent: VerticalAccent;
  copy: HeroCopy;
};

/** Map each accent hex to its halo color expressed as `rgba(r,g,b,0.18)`. */
const HALO_RGB: Record<VerticalAccent, string> = {
  '#22D3EE': '34,211,238', // cyan (retail)
  '#A78BFA': '167,139,250', // violet (services)
  '#FFB74D': '255,183,77', // amber (events)
  '#7FF4FF': '127,244,255', // light cyan (b2b)
};

const VerticalHero = ({ accent, copy }: Props) => {
  const halo = `radial-gradient(circle, rgba(${HALO_RGB[accent]},0.18) 0%, transparent 60%)`;
  const hasImage = Boolean(copy.image);

  return (
    <section
      aria-label={copy.sectionAriaLabel}
      className="relative w-full overflow-hidden px-6 pt-24 pb-20 lg:pt-32 lg:pb-24"
      style={{
        background: 'linear-gradient(180deg, #0A0E27 0%, #1B1238 100%)',
        color: '#F5F7FA',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '50%',
          right: '-160px',
          width: 560,
          height: 560,
          transform: 'translateY(-50%)',
          background: halo,
          pointerEvents: 'none',
        }}
      />

      <div className="relative mx-auto max-w-[1200px]">
        <div
          className={hasImage ? 'grid items-center gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,220px)] lg:gap-12' : ''}
        >
          <div>
            <div className="mono" style={{ fontSize: 12, letterSpacing: 2, color: accent, marginBottom: 16 }}>
              {copy.kicker}
            </div>
            <h1
              className="font-bold tracking-tight text-white"
              style={{
                fontSize: 'clamp(32px, 5.6vw, 64px)',
                lineHeight: 1.08,
                letterSpacing: '-0.02em',
                margin: 0,
                maxWidth: hasImage ? 720 : 880,
              }}
            >
              <span className="block">{copy.headlineA}</span>
              <span className="block" style={{ color: '#B4C1D8' }}>
                {copy.headlineB}
              </span>
            </h1>
            <p
              style={{
                color: '#B4C1D8',
                fontSize: 'clamp(16px, 1.6vw, 20px)',
                lineHeight: 1.5,
                margin: '20px 0 36px',
                maxWidth: hasImage ? 620 : 720,
              }}
            >
              {copy.subhead}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <CtaLink
                href={copy.ctaPrimary.href}
                className="sum-cta-primary"
                aria-label={copy.ctaPrimary.ariaLabel ?? copy.ctaPrimary.label}
              >
                {copy.ctaPrimary.label}
              </CtaLink>
              <CtaLink
                href={copy.ctaSecondary.href}
                className="sum-cta-ghost"
                aria-label={copy.ctaSecondary.ariaLabel ?? copy.ctaSecondary.label}
              >
                {copy.ctaSecondary.label}
              </CtaLink>
            </div>
          </div>

          {copy.image ? (
            <div className="order-last">
              <Image
                src={copy.image.src}
                alt={copy.image.alt}
                width={840}
                height={840}
                sizes="(min-width: 1024px) 220px, 50vw"
                className="mx-auto h-auto w-1/2 max-w-[180px] rounded-2xl lg:w-full lg:max-w-[220px]"
                priority
              />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default VerticalHero;
